import "../custom-elements/product-media"
import EVENTS from "../lib/events"
import { dialog } from "../lib/modals"

class MainProductDefault extends HTMLElement {
  constructor() {
    super()

    this.selectors = {
      productInner: ".product-inner",

      // Set sticky container
      detailsContainer: ".product__details",
      mediaContainer: ".product__media",

      // Variant change events
      buyButtonBlock: "product-block-buy-buttons",
      buyButtons: "[js-buy-buttons]",
      mediaBlock: "product-media",
      mediaItem: ".media",
      priceBlock: "[js-product-block-price]",
      productForm: "[data-product-form]",
      productFormIdInput: "input[name='id']",
      skuBlock: "[js-product-block-sku]",
      storeAvailabilityContainer: "[js-store-availability-container]",
      variantBlock: "product-block-variant-selector",
      variantMediaGrouping: "[data-enable-media-grouping='true']",
      variantSelectedInputs: "div input:checked",
    }

    this.classes = {
      changePending: "change-pending",
      hasError: "has-error",
      stickyDetailsContainer: "sticky-details-container",
      stickyMediaContainer: "sticky-media-container",
    }
  }

  connectedCallback() {
    this.controller = new AbortController()
    this.sectionId = this.getAttribute("data-section-id")
    this.productInner = this.querySelector(this.selectors.productInner)
    this.mediaContainer = this.querySelector(this.selectors.mediaContainer)
    this.detailsContainer = this.querySelector(this.selectors.detailsContainer)

    // Sticky container
    this.setStickyContainer()

    // Variant change handling selectors
    this.productForm = this.querySelector(this.selectors.productForm)
    this.buyButtonBlock = this.querySelector(this.selectors.buyButtonBlock)
    this.mediaBlock = this.querySelector(this.selectors.mediaBlock)
    this.variantBlock = this.querySelector(this.selectors.variantBlock)
    this.variantMediaGrouping = this.querySelector(
      this.selectors.variantMediaGrouping,
    )

    // Listen for variant change
    this.addEventListener(
      EVENTS.PRODUCT_VARIANT_CHANGING,
      (event) => {
        this.handleNewVariant(event.detail)
      },
      { signal: this.controller.signal },
    )
  }

  //
  // General setup functions
  //
  setStickyContainer() {
    this.stickyContainer = this.productInner.getAttribute(
      "data-sticky-effect-container",
    )
    this.galleryLayout = this.productInner.getAttribute("data-gallery-layout")

    if (
      this.stickyContainer === "gallery" &&
      (this.galleryLayout === "thumbnails_below" ||
        this.galleryLayout === "thumbnails_left")
    ) {
      this.mediaContainer.classList.add(this.classes.stickyMediaContainer)
      this.detailsContainer.classList.remove(
        this.classes.stickyDetailsContainer,
      )
    }

    if (this.stickyContainer === "product_details") {
      this.detailsContainer.classList.add(this.classes.stickyDetailsContainer)
      this.mediaContainer.classList.remove(this.classes.stickyMediaContainer)
    }
  }

  //
  // Variant on change functions
  //
  handleNewVariant(newElements) {
    this.detailsContainer.classList.add(this.classes.changePending)

    const newOptionInput = newElements.newOptionInput
    const newVariantId = newOptionInput.getAttribute("data-variant-id")

    const params = new URLSearchParams({
      sectionId: this.sectionId,
    })

    // Set params with new variant for fetch
    if (newVariantId) {
      params.set("variant", newVariantId)
    } else {
      // Build params from currently selected variants
      const selectedOptionValues = Array.from(
        this.querySelectorAll(this.selectors.variantSelectedInputs),
      )

        .map((element) => element.getAttribute("data-option-value-id"))
        .join(",")

      params.set("option_values", selectedOptionValues)
    }

    // URL only provided if connected to Shopify sibling products
    const newProductUrl = newOptionInput.getAttribute("data-product-url")

    if (!newVariantId && !newProductUrl) {
      this.setUnavailable()
      return
    }

    // Fetch new html to repaint applicable blocks
    fetch(`${newProductUrl}?${params}`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, "text/html")

        //
        // Non interactive content changes handled here
        //
        // Update url for history and deep linking
        this.updateUrlWithVariant(newVariantId)

        // Update html in product blocks
        const blockContentToReplace = [
          "variantBlock",
          "skuBlock",
          "priceBlock",
          "buyButtons",
          "storeAvailabilityContainer",
        ]

        window.flu.dialogs["storeAvailabilityDrawer"][1].destroy({
          deregister: true,
        })

        this.updateBlockHTML(blockContentToReplace, html)

        // Update product form's variant
        const htmlVariantId = html
          .querySelector(this.selectors.productForm)
          .getAttribute("data-current-product-id")

        this.productForm.setAttribute("data-current-product-id", htmlVariantId)
        this.productForm
          .querySelector(this.selectors.productFormIdInput)
          .setAttribute("value", htmlVariantId)

        //
        // Interactive content requiring events for repainting listeners
        //
        if (this.buyButtonBlock) {
          this.updateBuyButtonBlockContent()
        }

        if (this.variantBlock) {
          this.updateVariantSelectorBlockContent()
        }

        let hasMediaGrouping = false
        let newContent = newOptionInput
        if (this.variantMediaGrouping) {
          hasMediaGrouping = true
          newContent = html.querySelector(this.selectors.mediaBlock)
        }

        this.updateMediaBlockContent(newContent, hasMediaGrouping)
        this.detailsContainer.classList.remove(this.classes.changePending)
      })
  }

  getUrlWithVariant(url, id) {
    const parsedURL = new URL(url)
    const urlBase = parsedURL.origin + parsedURL.pathname
    const params = parsedURL.searchParams

    params.set("variant", id)

    return `${urlBase}?${params}`
  }

  setUnavailable() {
    const unavailableSelectors = [
      this.selectors.priceBlock,
      this.selectors.skuBlock,
    ]

    this.querySelectorAll(unavailableSelectors).forEach(({ classList }) =>
      classList.add("hidden"),
    )
    this.disableSubmitButtons()
    this.detailsContainer.classList.remove(this.classes.changePending)
  }

  disableSubmitButtons() {
    const unavailableString = window.theme.strings.products.product.unavailable
    const buyButtons = this.querySelector(this.selectors.buyButtons)
    const submitButtons = buyButtons.querySelectorAll("button")

    submitButtons.forEach((button) => {
      const submitButtonLabels = button.querySelectorAll(".button__label")

      button.setAttribute("disabled", "disabled")
      submitButtonLabels.forEach((label) => {
        label.textContent = unavailableString
      })
    })
  }

  updateUrlWithVariant(variantId) {
    const url = this.getUrlWithVariant(window.location.href, variantId)
    window.history.replaceState({ path: url }, "", url)
  }

  updateBlockHTML(contentToUpdateArray, html) {
    // TODO: Reassess approach, particularly for SKU where additional element
    // required so the innerHTML can be updated.
    contentToUpdateArray.forEach((block) => {
      this[block] = this.querySelector(this.selectors[block])
      this.htmlBlock = html.querySelector(this.selectors[block])

      if (this[block]) {
        this[block].outerHTML = this.htmlBlock.outerHTML
      }
    })
  }

  //
  // Send updated content via events for blocks to handle
  //
  updateBuyButtonBlockContent() {
    this.buyButtonBlock.classList.remove(this.classes.hasError)
    this.buyButtonBlock = this.querySelector(this.selectors.buyButtonBlock)

    // Register new dialog content
    dialog(this.buyButtonBlock.querySelector("dialog"))

    this.buyButtonBlock.dispatchEvent(
      new CustomEvent(EVENTS.PRODUCT_VARIANT_CHANGED),
    )
  }

  updateVariantSelectorBlockContent() {
    this.variantBlock.dispatchEvent(
      new CustomEvent(EVENTS.PRODUCT_VARIANT_CHANGED),
    )
  }

  updateMediaBlockContent(newContent, hasMediaGrouping) {
    // There's a split path depending on if Media grouping is enabled
    // Media grouping needs to update indicators and repaint the block
    if (hasMediaGrouping) {
      const mediaSize = parseInt(
        newContent.getAttribute("data-media-count"),
        10,
      )

      // TODO: Look into alternate approach in CSS to do the calcs with --mediaCount
      const mobileThumbnailWidthPercentage = (
        100 / parseInt(mediaSize, 10)
      ).toFixed(2)

      this.mediaBlock.style.setProperty(
        "--mobile-thumbnail-width-percentage",
        `${mobileThumbnailWidthPercentage}%`,
      )

      this.mediaBlock = this.querySelector(this.selectors.mediaBlock)
      this.mediaBlock.outerHTML = newContent.outerHTML

      // Reassign media block so it's pulling the most up to date one
      this.mediaBlock = this.querySelector(this.selectors.mediaBlock)
      this.mediaBlock.dispatchEvent(
        new CustomEvent(EVENTS.PRODUCT_VARIANT_CHANGED, {
          detail: {
            hasMediaGrouping: true,
            mediaSize,
          },
        }),
      )
    } else {
      // Media without grouping needs set navigate to featured media, properly
      // trigger slider events, styling and stay aware of other featured media
      const newFeaturedMedia = newContent.getAttribute(
        "data-option-value-featured-media",
      )

      if (newFeaturedMedia) {
        const variantMediaChangedEvent = new CustomEvent(
          EVENTS.PRODUCT_VARIANT_CHANGED,
          {
            bubbles: true,
            detail: {
              hasMediaGrouping: false,
              newFeaturedMedia,
            },
          },
        )

        this.mediaItem = this.querySelector(this.selectors.mediaItem)
        this.mediaItem.dispatchEvent(variantMediaChangedEvent)
      }
    }
  }

  disconnectedCallback() {
    this.controller.abort()
  }
}

if (!customElements.get("main-product-default")) {
  customElements.define("main-product-default", MainProductDefault)
}
